import { useAtlasSemanticThemeTokens } from '@issuemanager/apps/client/theme';
import { RICH_TEXT_SANITIZATION_CONFIG } from '@issuemanager/constants';
import { Box, Typography, TypographyProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import parse from 'html-react-parser';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import sanitizeHtml from 'sanitize-html';

const StyledContentBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'maxLines' &&
    prop !== 'isExpanded' &&
    prop !== 'linkFontWeight' &&
    prop !== 'linkColor',
})<{
  maxLines?: number;
  isExpanded: boolean;
  linkFontWeight: number | string;
  linkColor: string;
}>(({ maxLines, isExpanded, linkFontWeight, linkColor }) => ({
  width: '100%',
  overflowWrap: 'break-word',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  ...(maxLines &&
    !isExpanded && {
      display: '-webkit-box',
      WebkitLineClamp: maxLines,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    }),
  '& p': {
    margin: 0,
  },
  '& a': {
    fontWeight: linkFontWeight,
    color: linkColor,
  },
}));

const StyledExpandLink = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'linkColor',
})<TypographyProps & { linkColor: string }>(({ linkColor }) => ({
  fontWeight: 900,
  color: linkColor,
  cursor: 'pointer',
}));

interface RichTextDisplayProps {
  text: string;
  maxLines?: number;
  testId?: string;
}

export function RichTextDisplay({
  text,
  maxLines = 3,
  testId,
}: RichTextDisplayProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const semantic = useAtlasSemanticThemeTokens();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const sanitizedHtml = useMemo(() => {
    if (!text) return '';
    return sanitizeHtml(text, RICH_TEXT_SANITIZATION_CONFIG);
  }, [text]);

  useEffect(() => {
    if (!maxLines || !contentRef.current) {
      setIsTruncated(false);
      return;
    }

    const element = contentRef.current;
    setIsTruncated(element.scrollHeight > element.clientHeight);
  }, [sanitizedHtml, maxLines]);

  if (!text) {
    return <Box data-testid={testId}>-</Box>;
  }

  return (
    <Box data-testid={testId}>
      <StyledContentBox
        ref={contentRef}
        maxLines={maxLines}
        isExpanded={isExpanded}
        linkFontWeight={semantic.fontWeight.emphasis.value}
        linkColor={semantic.color.link.default.value}
      >
        {parse(sanitizedHtml)}
      </StyledContentBox>
      {isTruncated && (
        <StyledExpandLink
          component='span'
          linkColor={semantic.color.link.default.value}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {t(`truncatedText.${isExpanded ? 'showLess' : 'showMore'}`)}
        </StyledExpandLink>
      )}
    </Box>
  );
}
